import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { APIService } from "@utils/apiService";
import { AppConfig } from "@config/config";
import { enqueueSnackbarMessage } from "@slices/commonSlice/common";
import { AxiosError } from "axios";
import { ActivePromotionCycleInterface, PromotionRequest, PromotionType } from "@utils/types";

export interface IndividualContributorPromotionState {
  state: "failed" | "success" | "loading" | "idle";
  updateState: "failed" | "success" | "loading" | "idle";
  promotionRequests: PromotionRequest[];
  stateMessage: string | null;
  activeCycleId: string | null;
  successMessage: string | null;
}

const initialState: IndividualContributorPromotionState = {
  state: "idle",
  updateState: "idle",
  promotionRequests: [],
  stateMessage: null,
  successMessage: null,
  activeCycleId: null
};

export const updateRejectionReason = createAsyncThunk(
  "admin/updateRejectionReason",
  async (
    payload: {
      id: number;
      reasonForRejection: string,
    },
    { dispatch },
  ) => {
    return new Promise<{
      successMessage: string
    }>((resolve, reject) => {
      APIService.getInstance()
        .patch<{ status: string }>(
          AppConfig.serviceUrls.retrieveAllPromotionRequests,
          payload,
        )
        .then((resp) => {
           if (resp.status >= 200 && resp.status < 300){
            dispatch(
                enqueueSnackbarMessage({
                message:
                    String(resp.data.status),
                type: "success",
                }),
            );
           }
           resolve({
                successMessage: resp.data.status
           });
        })
        .catch((error: AxiosError) => {
          dispatch(
            enqueueSnackbarMessage({
              message:
                error.response?.status === 500
                  ? String(error.response?.data)
                  : "Unable to save the rejection reason",
              type: "error",
            }),
          );
          reject(error);
        });
    });
  },
);

export const getIndividualContributorPromotions = createAsyncThunk(
  "admin/getIndividualContributorPromotions",
  async (_, { dispatch }) => {
    return new Promise<{ promotionRequests: PromotionRequest[] }>(
      (resolve, reject) => {
        Promise.all([
            APIService.getInstance().get<{
              promotionCycles: ActivePromotionCycleInterface[];
            }>(AppConfig.serviceUrls.getActivePromotionCycle),
          ])
            .then(([open]) => {
              var promotionCycle = {
                ...open.data?.promotionCycles[0],
              };
        
              if (promotionCycle?.id === undefined) {
                resolve({ promotionRequests: [] });
              } else {
                APIService.getInstance()
                  .get<{ promotionRequests: PromotionRequest[] }>(
                    AppConfig.serviceUrls.retrieveAllPromotionRequests + 
                    "?&cycleId=" + promotionCycle?.id + 
                    "&type=" + PromotionType.INDIVIDUAL_CONTRIBUTOR,
                  )
                  .then((resp) => {
                    resolve({ 
                        promotionRequests: resp.data.promotionRequests
                    });
                  })
                  .catch((error: AxiosError) => {
                    dispatch(
                      enqueueSnackbarMessage({
                        message:
                          error.response?.status === 500
                            ? String(error.response?.data)
                            : "Unable to load requests",
                        type: "error",
                      }),
                    );
                    reject(error);
                  });
              }
            })
            .catch((error: Error) => {
              reject(error);
            });
      }
      
    );
  }
);

export const IndividualContributorPromotionSlice = createSlice({
  name: "individualContributorPromotion",
  initialState,
  reducers: {
    setCycleId: (state, action: PayloadAction<string | null>) => {
      state.activeCycleId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateRejectionReason.pending, (state) => {
        state.stateMessage = "Loading Requests";
        state.updateState = "loading";
      })
      .addCase(updateRejectionReason.fulfilled, (state, action) => {
        state.updateState = "success";
      })
      .addCase(updateRejectionReason.rejected, (state, action) => {
        state.updateState = "failed";
      })
      .addCase(getIndividualContributorPromotions.pending, (state) => {
        state.stateMessage = "Loading Requests";
        state.state = "loading";
      })
      .addCase(getIndividualContributorPromotions.fulfilled, (state, action) => {
        state.promotionRequests = action.payload.promotionRequests;
        state.state = "success";
      })
      .addCase(getIndividualContributorPromotions.rejected, (state, action) => {
        state.state = "failed";
      });
  },
});

export const { setCycleId } = IndividualContributorPromotionSlice.actions;

export default IndividualContributorPromotionSlice.reducer;
